import axiosInstance from '../../api/api';
import { putFileWithProgress } from './promptVideoUpload';

type PresignResponse = {
  uploadUrl: string;
  publicUrl: string;
  requiredHeaders?: Record<string, string>;
};

function normalizeDocumentContentType(file: File): string {
  const type = (file.type || '').toLowerCase().split(';')[0].trim();
  if (type) return type;
  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf')) return 'application/pdf';
  if (name.endsWith('.doc')) return 'application/msword';
  if (name.endsWith('.docx')) {
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  }
  if (name.endsWith('.txt')) return 'text/plain';
  return 'application/octet-stream';
}

export async function uploadPromptDocumentDirect(
  file: File,
  options?: {
    onProgress?: (progress: {
      phase: 'presigning' | 'uploading' | 'done';
      percent: number;
    }) => void;
    registerAbort?: (abort: () => void) => void;
  }
): Promise<string> {
  const contentType = normalizeDocumentContentType(file);
  options?.onProgress?.({ phase: 'presigning', percent: 2 });

  const { data } = await axiosInstance.post<PresignResponse>('prompt/document/presign', {
    content_type: contentType,
    filename: file.name,
  });

  if (!data?.uploadUrl || !data?.publicUrl) {
    throw new Error('Could not start document upload.');
  }

  const headers = {
    ...(data.requiredHeaders ?? {}),
    'Content-Type': contentType,
  };

  options?.onProgress?.({ phase: 'uploading', percent: 5 });
  const { promise, abort } = putFileWithProgress(data.uploadUrl, file, headers, (loaded, total) => {
    const ratio = total > 0 ? loaded / total : 0;
    const percent = Math.max(5, Math.min(95, Math.round(5 + ratio * 90)));
    options?.onProgress?.({ phase: 'uploading', percent });
  });
  options?.registerAbort?.(abort);
  await promise;

  options?.onProgress?.({ phase: 'done', percent: 100 });
  return data.publicUrl;
}
